import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Video, ChevronLeft, BookOpen } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINE_LABEL = {
  muay_thai_kickboxing: "Kickboxing / Muay Thaï",
  muay_thai: "Muay Thaï",
  kickboxing: "Kickboxing",
  jiu_jitsu_bresilien: "Jiu-Jitsu Brésilien",
  jiu_jitsu_kids: "Jiu-Jitsu Kids",
  jiu_jitsu_can_ryu: "Jiu-jitsu Can Ryu",
  mma: "MMA",
  autre: "Autre",
};

const CAT_LABEL = {
  coups_poing: "Coups de poing",
  coups_pied: "Coups de pied",
  coude_genou: "Coude / Genou",
  clinch: "Clinch",
  balayage: "Balayage",
  defense: "Défense",
  deplacement: "Déplacement",
  combinaison: "Combinaison",
  kata_forme: "Kata / Forme",
  sparring: "Sparring",
  conditioning: "Conditioning",
  soumission: "Soumission",
  projection: "Projection",
  control: "Contrôle",
  passage: "Passage de garde",
  garde: "Garde (système)",
  back_control: "Contrôle du dos",
  takedown: "Takedown",
  autre: "Autre",
};

export default function TechniquePublic() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/techniques/public/${id}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Technique introuvable.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-black flex items-center justify-center">
        <Loader2 className="animate-spin text-impact-gold" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-black flex items-center justify-center text-white text-center px-6">
        <div>
          <BookOpen size={64} className="mx-auto mb-4 opacity-40" />
          <h1 className="text-2xl font-display font-bold mb-2">Technique introuvable</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  const beltColor = data?.belt?.couleur_hex || "#d4af37";
  const videoEmbed = data?.video_url ? getEmbedUrl(data.video_url) : null;

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: `radial-gradient(ellipse at top, ${beltColor}22 0%, #0a0a0a 60%, #000 100%)` }}
      data-testid="technique-public-page"
    >
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Header dojo */}
        <div className="flex items-center gap-3 mb-8 pb-4 border-b border-white/10">
          <div className="w-10 h-10 rounded-full" style={{ background: beltColor, boxShadow: `0 0 24px ${beltColor}66` }} />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">{data?.dojo_name}</div>
            <div className="text-xs text-impact-gold tracking-wider uppercase">Bibliothèque Technique</div>
          </div>
        </div>

        {/* Ceinture + Discipline */}
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider">
          <span className="px-3 py-1 rounded-full" style={{ background: `${beltColor}22`, color: beltColor, border: `1px solid ${beltColor}55` }}>
            {data?.belt?.nom || `Niveau ${data?.niveau_ordre_requis}`}
          </span>
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-300">{DISCIPLINE_LABEL[data?.discipline] || data?.discipline}</span>
          {data?.categorie && (
            <>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-300">{CAT_LABEL[data.categorie] || data.categorie}</span>
            </>
          )}
        </div>

        {/* Nom */}
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2" data-testid="tech-title">
          {data?.nom}
        </h1>
        {data?.nom_alternatif && (
          <p className="text-zinc-400 italic mb-6">{data.nom_alternatif}</p>
        )}

        {/* Description */}
        {data?.description && (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/10">
            <h2 className="text-xs uppercase tracking-[0.3em] text-impact-gold mb-3">Description</h2>
            <p className="text-zinc-200 leading-relaxed text-base">{data.description}</p>
          </div>
        )}

        {/* Vidéos liées (Phase 3b — many-to-many) */}
        {data?.videos?.length > 0 ? (
          <div className="space-y-4 mb-6" data-testid="tech-videos-gallery">
            {data.videos.map((vid) => (
              <div key={vid.id} data-testid={`tech-video-${vid.id}`}>
                {(data.videos.length > 1 || vid.is_primary) && (
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-zinc-400">
                    {vid.is_primary && (
                      <span className="px-2 py-0.5 rounded-full bg-impact-gold/15 text-impact-gold border border-impact-gold/40">
                        Vidéo principale
                      </span>
                    )}
                    <span className="truncate">{vid.title}</span>
                  </div>
                )}
                <VideoBlock video={vid} title={vid.title || data?.nom} />
              </div>
            ))}
          </div>
        ) : data?.video_url && data.video_url.startsWith("/api/") ? (
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl mb-6 bg-black">
            <video
              src={`${process.env.REACT_APP_BACKEND_URL}${data.video_url}`}
              controls
              playsInline
              className="w-full aspect-video"
              data-testid="tech-video-player"
            />
          </div>
        ) : videoEmbed ? (
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl mb-6">
            <div className="aspect-video bg-black">
              <iframe
                src={videoEmbed}
                title={data?.nom}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="tech-video-iframe"
              />
            </div>
          </div>
        ) : data?.video_url ? (
          <a
            href={data.video_url}
            target="_blank"
            rel="noreferrer"
            data-testid="tech-video-link"
            className="flex items-center justify-center gap-3 bg-impact-gold/15 hover:bg-impact-gold/25 border border-impact-gold/40 text-impact-gold rounded-2xl py-4 mb-6 transition-colors"
          >
            <Video size={20} /> <span className="font-medium">Regarder la démonstration vidéo</span>
          </a>
        ) : (
          <div className="bg-white/5 rounded-2xl p-6 mb-6 border border-white/10 text-center text-zinc-500 text-sm">
            <Video size={32} className="mx-auto mb-2 opacity-40" />
            Aucune vidéo associée à cette technique pour le moment.
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-zinc-500 mt-12 pt-6 border-t border-white/10">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-1 hover:text-impact-gold transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft size={14} /> Retour
          </button>
          <div className="mt-3 text-[10px] tracking-[0.3em] uppercase opacity-60">
            Mon Assistant Pro · Bibliothèque Technique
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoBlock({ video, title }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  // Embed prioritaire (YouTube/Vimeo importé depuis la bibliothèque)
  const embed = video?.embed_url || (video?.video_url ? getEmbedUrl(video.video_url) : null);
  if (video?.video_url && video.video_url.startsWith("/api/")) {
    return (
      <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black">
        <video src={`${API_BASE}${video.video_url}`} controls playsInline className="w-full aspect-video" />
      </div>
    );
  }
  if (embed) {
    return (
      <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
        <div className="aspect-video bg-black">
          <iframe
            src={embed}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>
    );
  }
  if (video?.video_url) {
    return (
      <a href={video.video_url} target="_blank" rel="noreferrer"
        className="flex items-center justify-center gap-3 bg-impact-gold/15 hover:bg-impact-gold/25 border border-impact-gold/40 text-impact-gold rounded-2xl py-4 transition-colors">
        <Video size={20} /> <span className="font-medium">Regarder la démonstration vidéo</span>
      </a>
    );
  }
  return null;
}

function getEmbedUrl(url) {
  if (!url) return null;
  const u0 = String(url).trim();
  // Bunny Stream : URL d'embed déjà prête
  if (u0.includes("mediadelivery.net")) return u0;
  try {
    const u = new URL(u0);
    // Vimeo
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch {
    return null;
  }
  return null;
}
